import React, { useEffect, useState } from 'react';
import OutcomeWeight from 'components/OutcomeWeight';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import theme from 'theme';
import { Outcome, Plan, Checkin } from 'types';
import * as checkinUtils from 'utils/checkinUtils';
import * as membershipUtils from 'utils/membershipUtils';

import * as remoteApi from 'api/remote';
import { shallowEqual, useSelector } from 'react-redux';
import UserSelectButton from 'components/UserSelectButton';
import { useQueryCache, useMutation, useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import KoalaStackedAvatar from 'koala/components/StackedAvatars';
import OutcomeChildren from './OutcomeChildren';
import { useTranslation } from 'react-i18next';
// import BlockActions from 'components/BlockActions';
import { format } from 'date-fns';

// Components
import Parents from './Parents';
import KoalaIcon from 'koala/components/Icons';
import OutcomeTarget from 'components/OutcomeTarget';
import {
  getExpectedValuesAtDates,
  cleanFormat,
  getLatestCheckinByWeek,
  getLatestCheckinByMonth,
} from 'utils/metricsUtils';
const Container = styled.div`
  .show-on-hover {
    opacity: 0;
    transition: all ease 0.2s;
  }

  &:hover {
    .show-on-hover {
      opacity: 1;
    }
  }
`;

const MetricRow = styled.div`
  display: grid;

  grid-template-columns: 3.2rem 20rem 9rem 1fr;
  grid-template-areas: 'gutter metric owners dates';

  .block-actions {
    opacity: 0;
    z-index: 2;
  }

  &:hover {
    background: ${theme.colors.N3};
    cursor: pointer;
    .block-actions {
      opacity: 1;
    }
  }

  header {
    font-size: 1.2rem;
    color: ${theme.colors.subtleText};
  }

  &.header {
    &:hover {
      background: none;
    }
  }
`;

const BlockValues = styled.div`
  grid-area: dates;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.N10};
  padding: 0;

  &:hover {
    background: ${theme.colors.N3};
    cursor: pointer;
  }

  > div {
    display: flex;
    &:first-of-type {
      border-bottom: 1px solid ${theme.colors.blockBorder};
    }
    &:last-of-type {
      border-bottom: 0;
    }
  }

  header,
  .values {
    min-width: 10rem;
    width: 10rem;
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    padding: ${theme.spacing.x1};
  }
  .values {
    font-weight: 600;
  }
`;

const BlockMetric = styled.div`
  border-bottom: 1px solid ${theme.colors.N10};

  grid-area: metric;
  position: sticky;
  left: 0;
  z-index: 1;
  border-right: 1px solid ${theme.colors.blockBorder};
  background: ${theme.colors.N0};
  padding: ${theme.spacing.x1};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.half};
  > div {
    display: flex;
    gap: ${theme.spacing.x1};
    align-items: center;
    &.metric-target {
      color: ${theme.colors.subtleText};
    }
  }
`;

const ChildrenContainer = styled.div`
  border-top: 1px solid ${theme.colors.blockBorder};
`;

const BlockGutter = styled.div`
  border-bottom: 1px solid ${theme.colors.N10};
  grid-area: gutter;
  display: flex;
  width: 3.2rem;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #f3f3f3;
    cursor: pointer;
  }
  .expand-gutter {
    transition: all ease 0.2s;
  }
  div.expanded {
    transform: rotate(90deg);
  }

  @media ${theme.devices.mobile} {
    display: none;
  }
`;

export const BlockContent = styled.div`
  font-weight: 400;
  grid-area: title;
  background: transparent;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.x1};
  min-width: 0rem;

  .color-block {
    min-width: 20px;
  }

  a {
    &:hover {
      text-decoration: none;
    }
  }

  @media ${theme.devices.mobile} {
    padding-left: 1.2rem;
  }
`;

const BlockOwners = styled.div`
  border-bottom: 1px solid ${theme.colors.N10};

  grid-area: owners;
  display: flex;
  align-items: center;
  justify-content: left;
  flex-direction: row-reverse;
  padding: ${theme.spacing.x1};
  border-right: 1px solid ${theme.colors.blockBorder};

  .contributors {
    margin-left: -12px;
  }

  @media ${theme.devices.smallDesktop} {
    display: none;
  }
`;

interface Props {
  outcome: Outcome;
  defaultExpanded?: boolean;
  hideClosedInitiatives?: boolean;
  hideOwners?: boolean;
  showPlan?: boolean;
  hideExpand?: boolean;
  hideTags?: boolean;
  hideActions?: boolean;
  outcomeIndex?: string;
  hasEditPermission?: boolean;
  showParentsLabel?: boolean;
  setDefaultChanged?: (value: boolean) => void;
  defaultChanged?: boolean;
  metricDates: Date[];
  plan: Plan;
  kpiView: string;
}

interface MetricHeaderProps {
  metricDates: Date[];
}

export function MetricHeader(props: MetricHeaderProps) {
  const { metricDates } = props;
  const { t } = useTranslation();
  return (
    <Container>
      <MetricRow className="header">
        <BlockMetric>
          <header>{t('shared.terms.metric')}</header>
        </BlockMetric>
        <BlockOwners>
          <header>{t('shared.terms.owners')}</header>
        </BlockOwners>
        <BlockValues>
          <div>
            <header />
            {metricDates?.map((date) => (
              <header key={date.toISOString()}>{format(date, 'MMM d')}</header>
            ))}
          </div>
        </BlockValues>
      </MetricRow>
    </Container>
  );
}

function MetricExpandable(props: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const queryCache = useQueryCache();
  const params = new URLSearchParams(location.search);
  const isOnboardingRoute = params.get('onboarding') === '1';

  const {
    outcome,
    defaultExpanded,
    hideClosedInitiatives,
    hideTags,
    outcomeIndex,
    hasEditPermission,
    showParentsLabel,
    setDefaultChanged,
    defaultChanged,
    metricDates,
    plan,
    kpiView,
  } = props;
  const { nano_slug, membership } = outcome;

  const history = useHistory();
  const currentMembership = useSelector((state: any) => state.session.currentMembership, shallowEqual);
  const isReadOnly = membershipUtils.isReadOnly(currentMembership);

  // Expands the first outcome if we're on the onboarding route
  //const shouldBeExpanded = (defaultExpanded ?? false) || (isOnboardingRoute && outcomeIndex === '0-0');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);

  const hashPath = `#outcome:${nano_slug}:show`;
  const handleOutcomeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(hashPath);
  };

  useEffect(() => {
    // when main expand/collapse triggered it will set default changed to false
    // so if default changed is false, reset isExpanded back to default
    if (!defaultChanged) {
      setIsExpanded(defaultExpanded ?? false);
    }
  }, [defaultChanged, defaultExpanded]);

  useEffect(() => {
    const shouldBeExpanded = (defaultExpanded ?? false) || (isOnboardingRoute && outcomeIndex === '0-0');
    if (shouldBeExpanded) {
      setIsExpanded(true);
      if (setDefaultChanged) {
        setDefaultChanged(true);
      }
    }
  }, [defaultExpanded, isOnboardingRoute, outcomeIndex, setDefaultChanged]);

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
    if (setDefaultChanged) {
      setDefaultChanged(true);
    }
  };

  const [updateOutcomeMutation] = useMutation(remoteApi.updateOutcome, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.currentOutcome);
      queryCache.invalidateQueries(queryKeys.outcomes);
    },
  });

  // Construct the query key using the plan Id
  const queryKeyCheckins = [queryKeys.checkins, outcome.nano_slug];

  // Get the plan details
  const { data: checkinsResponse } = useQuery(queryKeyCheckins, remoteApi.fetchCheckins, {
    staleTime: 5 * 60 * 1000,
  });

  let checkins: Checkin[] = [];
  if (checkinsResponse) {
    checkins = checkinsResponse.data;
  }

  const handleAssignUser = (value: string) => {
    const membership_id = value;
    if (membership_id) {
      const params = {
        membership_id,
      };

      updateOutcomeMutation({
        outcomeId: outcome.nano_slug,
        outcome: params,
      });
    }
  };

  const handleUnassignUser = () => {
    const params = {
      membership_id: null,
    };
    updateOutcomeMutation({
      outcomeId: outcome.id,
      outcome: params,
    });
  };

  const expandedClassName = isExpanded ? 'expanded' : '';
  const hideTagsClassName = hideTags ? 'hide-tags' : '';

  let indexClassname = outcomeIndex ? `outcome-${outcomeIndex}` : '';

  const expectedValues = getExpectedValuesAtDates(outcome, plan, metricDates);
  let checkinValues: any[] = [];
  if (kpiView === 'week') {
    checkinValues = getLatestCheckinByWeek(checkins, metricDates);
  } else {
    checkinValues = getLatestCheckinByMonth(checkins, metricDates);
  }

  return (
    <Container className={`outcome-expanded ${indexClassname}`}>
      <MetricRow
        className={`outcome-row ${hideTagsClassName} outcome-${outcome.nano_slug}`}
        onClick={handleOutcomeClick}
        id={nano_slug}
      >
        <BlockGutter onClick={toggleExpand}>
          <KoalaIcon iconSize="xsmall" className={`${expandedClassName} expand-gutter`} iconName="rightChevron" />
        </BlockGutter>

        <BlockMetric>
          <div>
            <OutcomeWeight outcome={outcome} />
            <Link to={hashPath}>{outcome.title}</Link>
            {showParentsLabel && <Parents outcomeId={outcome.id} />}
          </div>
          <div className="metric-target">
            <OutcomeTarget outcome={outcome} />
          </div>
        </BlockMetric>
        <BlockOwners className="outcome-row--owners">
          <KoalaStackedAvatar
            owner={membership ?? null}
            contributors={outcome.contributors}
            size="small"
            hideOwner={true}
            className="contributors"
            direction="right"
            showNumber={true}
          />
          <UserSelectButton
            canEdit={!isReadOnly && hasEditPermission === true}
            handleAssignUser={handleAssignUser}
            selectedUser={membership ?? null}
            handleUnassignUser={handleUnassignUser}
            size="small"
          />
        </BlockOwners>
        <BlockValues>
          <div>
            <header>Target</header>
            {expectedValues?.map((expectedValue) => (
              <header key={expectedValue.date.toISOString()}>
                {outcome.outcome_type === 'stay_above' && <>&gt;</>}
                {outcome.outcome_type === 'stay_below' && <>&lt;</>}
                {['improve_metric', 'stay_above', 'stay_below'].includes(outcome.outcome_type) && (
                  <>{checkinUtils.formattedScore(cleanFormat(outcome.score_format), expectedValue.expected)}</>
                )}
                {outcome.outcome_type === 'kpi' && <>-</>}
                {outcome.outcome_type === 'no_metric' && <>on track</>}
              </header>
            ))}
          </div>
          <div>
            <span className="values">Actual</span>
            {checkinValues?.map((checkinValue) => {
              if (!checkinValue.checkin) {
                return (
                  <span className="values" key={checkinValue.date.toISOString()}>
                    -
                  </span>
                );
              }

              const score = checkinValue.checkin?.score;

              return (
                <span
                  className="values"
                  key={checkinValue.date.toISOString()}
                  style={{
                    backgroundColor: checkinUtils.confidenceToBackgroundColor(checkinValue.checkin?.confidence),
                    color: checkinUtils.confidenceToDarkColor(checkinValue.checkin?.confidence),
                  }}
                >
                  {outcome.outcome_type === 'no_metric' && (
                    <>{checkinUtils.confidenceToScore(checkinValue.checkin?.confidence, t)}</>
                  )}
                  {outcome.outcome_type !== 'no_metric' && (
                    <>{score ? checkinUtils.formattedScore(cleanFormat(outcome.score_format), score) : ''}</>
                  )}
                </span>
              );
            })}
          </div>
        </BlockValues>
      </MetricRow>
      {isExpanded && (
        <ChildrenContainer>
          <OutcomeChildren outcomeId={outcome.id} hideClosedInitiatives={hideClosedInitiatives ?? false} />
        </ChildrenContainer>
      )}
    </Container>
  );
}

export default MetricExpandable;
